import { createAsyncThunk } from "@reduxjs/toolkit";
import { uploadsServer } from "~/constants";

import type { Log, UploadProcess } from "~/types/tasks";
import { updateSingleThunk } from "./updateThunks";
import type { DbDataType } from "~/types/general";



interface UploadImagesPayload {
  files: File[];
  processObject: UploadProcess;
}
interface UploadFilePayload {
  file: File;
  processObject: UploadProcess;
}
interface UploadImageProps {
  file: File;
  bucketName: string;
  filePath: string;
  dataType?: DbDataType;
  id?: number;
}
const createFormDataFromObject = (object: any) => {
  const formData = new FormData();
  for (const key in object) {
    formData.append(key, object[key]);
  }
  return formData;
}
async function uploadFilesChunk(formData: FormData) {
  try {
    const response = await fetch(
      `${uploadsServer}files/${formData.get('logId')}`,
      {
        method: "POST",
        body: formData,
        // Do NOT set Content-Type for FormData; browser will set it with boundary
      }
    );
    if (!response.ok) {
      throw new Error("Failed to upload files");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error uploading files:", error);
    throw error;
  }
}

export const uploadFileThunk = createAsyncThunk(
  "upload/uploadFile",
  async ({ file, processObject }: UploadFilePayload, { rejectWithValue, signal, dispatch }) => {
    try {
      console.log({file, processObject})
      const newFormData = createFormDataFromObject(processObject);
      newFormData.append("file", file);
      const url = `${uploadsServer}file`
      // debug formData
      // console.log("uploadFileThunk formData:", Array.from(newFormData.entries()));
      const response = await fetch(url, {
        method: "POST",
        body: newFormData,
      });
      if (!response.ok) {
        throw new Error("Failed to create log");
      }
      const data = await response.json();
      console.log("uploadThunk data:", data);
      // no cache reset here: the uploads service tells dataCenter what changed
      return data;
    } catch (err) {
      console.error("uploadThunk error:", err);
      return rejectWithValue(err instanceof Error ? err.message : "Unknown error");
    }
  }
);
export const uploadImagesThunk = createAsyncThunk(
  "upload/uploadImages",
  async ({ files, processObject }: UploadImagesPayload, { rejectWithValue, signal  }) => {
    console.log({signal})
    const filesPerRequest = 1;
    const totalFiles = files.length;
    const totalRequests = Math.ceil(totalFiles / filesPerRequest);
    let requestNumber = 1;
    let start = 0;
    let end = start + filesPerRequest;

    while (start < totalFiles) {
      if (end > totalFiles) {
        end = totalFiles;
      }

      const newFormData = createFormDataFromObject(processObject);
      newFormData.append("totalRequests", String(totalRequests));

      for (let i = start; i < end; i++) {
        const file = files[i];
        newFormData.append("files", file);
      }

      newFormData.append("requestNumber", String(requestNumber));

      try {
        const response = await uploadFilesChunk(newFormData);
        console.log(response);
        const temp = `Request ${requestNumber}/${totalRequests} uploaded successfully`;
        console.log(temp);
        requestNumber++;
        start = end;
        end = start + filesPerRequest;
      } catch (error) {
        console.error("Error uploading files:", error);
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
      }
    }
    return { success: true };
  }
);

interface UploadZipPayload {
  selectedFile: File;
  processObject: UploadProcess;
}

async function uploadChunk(formData: FormData) {
  try {
    const response = await fetch(
      `${uploadsServer}chunk`,
      {
        method: "POST",
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error("Failed to upload chunk");
    }
    return await response.json();
  } catch (error) {
    console.error("Error uploading chunk:", error);
    throw error;
  }
}

export const uploadZipThunk = createAsyncThunk(
  "upload/uploadZip",
  async ({ selectedFile, processObject }: UploadZipPayload, { rejectWithValue }) => {
    const chunkSize = 5 * 1024 * 1024; // 5MB
    const totalRequests = Math.ceil(selectedFile.size / chunkSize);
    let requestNumber = 1;
    let start = 0;
    let end = start + chunkSize;

    while (start < selectedFile.size) {
      const newFormData = createFormDataFromObject(processObject);
      newFormData.append("totalRequests", String(totalRequests));
      if (end > selectedFile.size) {
        end = selectedFile.size;
      }
      const chunk = selectedFile.slice(start, end);
      newFormData.append("file", chunk);
      newFormData.append("requestNumber", String(requestNumber));

      try {
        const data = await uploadChunk(newFormData);
        if (data.currentChunk) {
          requestNumber = data.currentChunk;
          start = requestNumber * chunkSize;
          end = start + chunkSize;
          requestNumber++;
          // console.log(`Chunk ${requestNumber}/${totalRequests} uploaded successfully`);
        } else {
          // console.log(`Chunk ${requestNumber}/${totalRequests} uploaded successfully`);
          requestNumber++;
          start = end;
          end = start + chunkSize;
        }
      } catch (error) {
        console.error("Error uploading chunk:", error);
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
      }
    }
    // console.log("File upload completed");
    return { success: true };
  }
);

export const cancelProcessThunk = createAsyncThunk(
  "upload/cancelProcess",
  async (logId: number, { rejectWithValue }) => {
    console.log("Cancelling process with logId:", logId);
    const url = `${uploadsServer}cancel`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: logId
        }),
      });
      if (!response.ok) throw new Error("Failed to cancel process");
      const data = await response.json();
      console.log("cancelProcess response:", data);
      return data;
    } catch (err) {
      console.error("cancelProcess error:", err);
      return rejectWithValue(err instanceof Error ? err.message : "Unknown error");
    }
  }
);

export const uploadImageThunk = createAsyncThunk(
  "upload/uploadProfileImage",
  async ({ file, bucketName, filePath, dataType, id }: UploadImageProps, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bucketName", bucketName);
      formData.append("filePath", filePath);


      const url = `${uploadsServer}image`;
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload profile image");
      }

      const data = await response.json();
      console.log("uploadImageThunk data:", data);
      if (!data.url) {
        throw new Error("No URL returned from upload");
      }
      if (id && dataType) {
        const updatedItem = await dispatch(updateSingleThunk({
            dataType,
            data: dataType === 'assets' ? { image_url: data.url, bid: id } : { profile_image: data.url, id },
          })).unwrap();
        // console.log("Updated item with new profile image:", updatedItem);
  
        return updatedItem;

      }
        return data.url;
    } catch (err) {
      console.error("uploadImageThunk error:", err);
      return rejectWithValue(err instanceof Error ? err.message : "Unknown error");
    }
  }
);

// Example usage for cancellation in a component:
// const controller = new AbortController();
// dispatch(uploadZipThunk({ selectedFile, processObject, signal: controller.signal }));
// To cancel:
// dispatch(cancelProcessThunk({ logId }));
// controller.abort();
